import React, {useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {
    Box,
    Heading,
    Text,
    Stack,
    Grid,
    SimpleGrid,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getCreditCardIcon} from '@salesforce/retail-react-app/app/utils/cc-utils'
import {orderDetailsGridStyle, orderSummaryStyle, tileStyle} from './detail.style'
import OrderSummary from '../order-summary'
import {pxToRem} from '../../utills/pxToRem'

export const OrderDetail = ({isLoading, order}) => {
    const {formatMessage} = useIntl()
    const shipments = order?.shipments
    const paymentCard = order?.paymentInstruments?.[0]?.paymentCard
    const CardIcon = getCreditCardIcon(paymentCard?.cardType)
    const currency = order?.currency || 'USD'

    const totalGiftCardValue = useMemo(() => {
        if (!order?.paymentInstruments) return 0

        return order.paymentInstruments?.reduce((total, instrument) => {
            if (instrument?.paymentMethodId === 'GIFT_CERTIFICATE') {
                const amount = parseFloat(instrument.amount) || 0 // adjust if `amount` is stored differently
                return total + amount
            }
            return total
        }, 0)
    }, [order?.paymentInstruments])

    const evaluateSubtotal = useMemo(() => {
        if (order && !isLoading) {
            let productSubTotal = 0
            // Handle regular product items
            if (order.productItems) {
                productSubTotal += parseFloat(order?.productSubTotal || 0)
            }
            // Handle gift certificate items
            if (order?.giftCertificateItems && order?.giftCertificateItems.length > 0) {
                // Add gift certificate amounts to subtotal
                const giftCertificateTotal = order?.giftCertificateItems.reduce(
                    (total, item) => total + parseFloat(item.amount || 0),
                    0
                )
                productSubTotal += giftCertificateTotal
            }
            return productSubTotal.toFixed(2)
        }
        return 0
    }, [order, isLoading])

    const getFormatedCardExpiryDate = useCallback(
        (dateStr) => {
            if (dateStr) {
                const [mm, yyyy] = dateStr.split('/')
                return [mm.padStart(2, '0'), yyyy.slice(-2)].join('/')
            }
            return ''
        },
        [order, isLoading]
    )

    return (
        <Box {...tileStyle}>
            <Grid
                templateColumns={{base: '1fr', lg: '50% 1fr', xl: '60% 1fr'}}
                gap={{base: 6, xl: 2}}
            >
                <SimpleGrid
                    columns={{base: 1, sm: 2}}
                    columnGap={pxToRem(25)}
                    rowGap={pxToRem(25)}
                    sx={orderDetailsGridStyle}
                >
                    {isLoading ? (
                        <>
                            <Stack gap={3}>
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="112px" />
                                <Skeleton h="20px" w="56px" />
                            </Stack>
                            <Stack gap={3}>
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="56px" />
                            </Stack>
                            <Stack gap={3}>
                                <Skeleton h="20px" w="112px" />
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="56px" />
                            </Stack>
                            <Stack gap={3}>
                                <Skeleton h="20px" w="60px" />
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="56px" />
                            </Stack>
                        </>
                    ) : (
                        <>
                            <Stack className="detail-item" order={{base: 0, lg: 0}}>
                                <Heading as="h2" fontSize="sm" margin="0 !important">
                                    <FormattedMessage
                                        defaultMessage="Shipping Method"
                                        id="account_order_detail.heading.shipping_method"
                                    />
                                </Heading>
                                {shipments?.map(
                                    (
                                        {
                                            shippingMethod,
                                            trackingNumber,
                                            shippingStatus,
                                            c_shipmentType,
                                            shippingAddress
                                        },
                                        ii
                                    ) => {
                                        if (
                                            c_shipmentType === 'gift' ||
                                            !shippingAddress?.address1
                                        ) {
                                            return null
                                        }
                                        return (
                                            <Box margin="0 !important" key={ii}>
                                                <Text fontSize="sm">{shippingMethod?.name}</Text>
                                                <Text fontSize="sm" textTransform="titlecase">
                                                    {
                                                        {
                                                            not_shipped: formatMessage({
                                                                defaultMessage: 'Not Yet Shipped',
                                                                id: 'account_order_detail.shipping_status.not_yet_shipped'
                                                            }),

                                                            part_shipped: formatMessage({
                                                                defaultMessage: 'Partially Shipped',
                                                                id: 'account_order_detail.shipping_status.part_shipped'
                                                            }),
                                                            shipped: formatMessage({
                                                                defaultMessage: 'Shipped',
                                                                id: 'account_order_detail.shipping_status.shipped'
                                                            })
                                                        }[shippingStatus]
                                                    }
                                                </Text>
                                                <Text fontSize="sm">
                                                    <FormattedMessage
                                                        defaultMessage="Tracking Number"
                                                        id="account_order_detail.label.tracking_number"
                                                    />
                                                    :{' '}
                                                    {trackingNumber ||
                                                        formatMessage({
                                                            defaultMessage: 'Pending',
                                                            id: 'account_order_detail.label.pending_tracking_number'
                                                        })}
                                                </Text>
                                            </Box>
                                        )
                                    }
                                )}
                            </Stack>
                            {order?.paymentInstruments && (
                                <Stack className="detail-item" order={{base: 2, lg: 1}}>
                                    <Heading as="h2" fontSize="sm">
                                        <FormattedMessage
                                            defaultMessage="Payment Method"
                                            id="account_order_detail.heading.payment_method"
                                        />
                                    </Heading>
                                    {order?.paymentInstruments?.map((instrument, index) => (
                                        <Box key={index} marginRight="1rem">
                                            {instrument?.paymentMethodId === 'GIFT_CERTIFICATE' && (
                                                <Text fontSize="sm" fontWeight="medium">
                                                    <FormattedMessage
                                                        defaultMessage="GIFT CARD: "
                                                        id="account_order_detail.payment_method.gift_card"
                                                    />
                                                </Text>
                                            )}
                                            <Box>
                                                {instrument?.paymentMethodId ===
                                                    'GIFT_CERTIFICATE' && (
                                                    <Text fontSize="sm">
                                                        {instrument.c_cayanGiftCardMasked}
                                                    </Text>
                                                )}
                                                {instrument?.paymentMethodId === 'PayPal' && (
                                                    <Text fontSize="sm" fontWeight="medium">
                                                        {instrument.paymentMethodId}
                                                    </Text>
                                                )}
                                                {instrument?.paymentMethodId === 'AdyenComponent' &&
                                                    CardIcon && (
                                                        <CardIcon
                                                            layerStyle="ccIcon"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                {instrument?.paymentMethodId ===
                                                    'AdyenComponent' && (
                                                    <Text as="span" fontSize="sm">
                                                        &nbsp;{instrument.paymentCard?.cardType}
                                                    </Text>
                                                )}
                                                {instrument?.paymentMethodId ===
                                                    'AdyenComponent' && (
                                                    <Text fontSize="sm" pl="38px">
                                                        &bull;&bull;&bull;&bull;{' '}
                                                        {instrument?.c_cardNo}{' '}
                                                        {getFormatedCardExpiryDate(
                                                            instrument?.c_expiryDate
                                                        )}
                                                    </Text>
                                                )}
                                                {instrument?.amount && (
                                                    <Text fontSize="sm">
                                                        <Text as="span" fontWeight="medium">
                                                            <FormattedMessage
                                                                defaultMessage="Amount: "
                                                                id="account_order_detail.payment_method.gift_card.amount"
                                                            />
                                                        </Text>{' '}
                                                        <FormattedNumber
                                                            style="currency"
                                                            currency={currency}
                                                            value={instrument.amount}
                                                        />
                                                    </Text>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                            <Stack className="detail-item" order={{base: 1, lg: 2}}>
                                <Heading as="h2" fontSize="sm" pt={1}>
                                    <FormattedMessage
                                        defaultMessage="Shipping Address"
                                        id="account_order_detail.heading.shipping_address"
                                    />
                                </Heading>
                                {shipments?.map(({shippingAddress, c_shipmentType}, ii) => {
                                    if (c_shipmentType === 'gift' || !shippingAddress?.address1) {
                                        return null
                                    }
                                    return (
                                        <Box key={ii}>
                                            <Text fontSize="sm">
                                                {shippingAddress.firstName}{' '}
                                                {shippingAddress.lastName}
                                            </Text>
                                            <Text fontSize="sm">{shippingAddress.address1}</Text>
                                            <Text fontSize="sm">
                                                {shippingAddress.city}, {shippingAddress.stateCode}{' '}
                                                {shippingAddress.postalCode}
                                            </Text>
                                        </Box>
                                    )
                                })}
                            </Stack>
                            <Stack className="detail-item" order={{base: 3, lg: 3}}>
                                <Heading as="h2" fontSize="sm" pt={1}>
                                    <FormattedMessage
                                        defaultMessage="Billing Address"
                                        id="account_order_detail.heading.billing_address"
                                    />
                                </Heading>
                                <Box>
                                    <Text fontSize="sm">
                                        {order.billingAddress.firstName}{' '}
                                        {order.billingAddress.lastName}
                                    </Text>
                                    <Text fontSize="sm">{order.billingAddress.address1}</Text>
                                    <Text fontSize="sm">
                                        {order.billingAddress.city},{' '}
                                        {order.billingAddress.stateCode}{' '}
                                        {order.billingAddress.postalCode}
                                    </Text>
                                </Box>
                            </Stack>
                        </>
                    )}
                </SimpleGrid>

                {!isLoading ? (
                    <Box sx={orderSummaryStyle}>
                        <OrderSummary
                            basket={{...order, c_orderTotal: evaluateSubtotal}}
                            totalGiftCardValue={totalGiftCardValue}
                            hideNote
                            addDivider
                        />
                    </Box>
                ) : (
                    <Skeleton h="full" />
                )}
            </Grid>
        </Box>
    )
}

OrderDetail.propTypes = {
    isLoading: PropTypes.bool,
    order: PropTypes.any
}
